import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

export type PDFViewerContextType = {
  scale: number;
  updateScale: (scale: number) => void;
  numPages: number;
  currentPage: number;
  changePage: (pageNumber: number) => void;
  isOutlineOpen: boolean;
  setIsOutlineOpen: (visible: boolean) => void;
};

const defaultPDFViewerContext: PDFViewerContextType = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  changePage: () => {},
  currentPage: 0,
  isOutlineOpen: false,
  numPages: 0,
  scale: 0,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIsOutlineOpen: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateScale: () => {},
};

const PDFViewerControlContext = createContext<PDFViewerContextType>(defaultPDFViewerContext);

type PDFViewerControlProviderProps = {
  children: ReactNode;
} & PDFViewerContextType;

export const PDFViewerControlProvider = memo(
  ({
    children,
    currentPage,
    changePage,
    numPages,
    setIsOutlineOpen,
    isOutlineOpen,
    updateScale,
    scale,
  }: PDFViewerControlProviderProps) => {
    const value = useMemo<PDFViewerContextType>(
      () => ({
        changePage,
        currentPage,
        isOutlineOpen,
        numPages,
        scale,
        setIsOutlineOpen,
        updateScale,
      }),
      [changePage, currentPage, isOutlineOpen, numPages, scale, setIsOutlineOpen, updateScale],
    );

    return <PDFViewerControlContext.Provider value={value}>{children}</PDFViewerControlContext.Provider>;
  },
);

PDFViewerControlProvider.displayName = 'PDFViewerControlProvider';

export const usePDFViewerControlContext = () => {
  const context = useContext(PDFViewerControlContext);
  if (context === defaultPDFViewerContext) {
    throw new Error('usePDFViewerControlContext must be used within a PDFViewerControlProvider');
  }
  return context;
};
